/**
 * Klassenuebersicht (Class Overview) functions for Fehlzeiten service layer.
 *
 * Handles all class-level statistics and summary queries.
 * NO direct Supabase imports should exist in UI components.
 *
 * @module fehlzeiten/services/klassenuebersicht
 */

import { supabase } from "../../../../lib/supabase";
import type {
  KlassenuebersichtParams,
  KlassenuebersichtRow,
  StudentAbsenceDetailsParams,
  StudentAbsenceDetail,
} from "../../shared/api";
import {
  mapKlassenuebersichtRow,
  mapStudentAbsenceDetailRow,
} from "../../shared/api/helpers";

// ============================================================================
// Class Overview Queries
// ============================================================================

/**
 * Fetches complete class overview with absence statistics for all students.
 *
 * This replaces 30+ individual API calls with a single RPC that aggregates
 * all student statistics for the selected class and date range.
 *
 * @param params - Class ID and date range
 * @returns Array of student summary rows
 *
 * @example
 * const summaries = await fetchKlassenuebersicht({
 *   classId: 'class-uuid',
 *   startDate: '2026-01-01',
 *   endDate: '2026-06-30',
 *   includeFullDayLessons: true,
 * });
 */
export async function fetchKlassenuebersicht(
  params: KlassenuebersichtParams,
): Promise<KlassenuebersichtRow[]> {
  const {
    classId,
    startDate,
    endDate,
    includeFullDayLessons = true,
    includeZeugnisExcluded = false,
  } = params;

  if (!classId || !startDate || !endDate) {
    return [];
  }

  const { data, error } = await supabase.rpc(
    "fn_get_klassenuebersicht_complete" as never,
    {
      p_class_id: classId,
      p_start_date: startDate,
      p_end_date: endDate,
      p_include_full_day_lessons: includeFullDayLessons,
      p_include_zeugnis_excluded: includeZeugnisExcluded,
    } as never,
  );

  if (error) {
    throw new Error(`Failed to fetch class overview: ${error.message}`);
  }

  return (Array.isArray(data) ? data : []).map((row) =>
    mapKlassenuebersichtRow(row as Record<string, unknown>),
  );
}

// ============================================================================
// Student Detail Queries
// ============================================================================

/**
 * Fetches detailed absence records for a specific student.
 *
 * Used when expanding a student row to show individual absence entries.
 *
 * @param params - Student ID, date range, and detail type
 * @returns Array of absence detail records
 *
 * @example
 * const details = await fetchStudentAbsenceDetails({
 *   studentId: 'student-uuid',
 *   startDate: '2026-01-01',
 *   endDate: '2026-06-30',
 *   detailType: 'days',
 * });
 */
export async function fetchStudentAbsenceDetails(
  params: StudentAbsenceDetailsParams,
): Promise<StudentAbsenceDetail[]> {
  const {
    studentId,
    startDate,
    endDate,
    detailType,
    includeFullDayLessons = true,
  } = params;

  if (!studentId || !startDate || !endDate) {
    return [];
  }

  const { data, error } = await supabase.rpc(
    "fn_get_student_absence_details" as never,
    {
      p_student_id: studentId,
      p_start_date: startDate,
      p_end_date: endDate,
      p_detail_type: detailType,
      p_include_full_day_lessons: includeFullDayLessons,
    } as never,
  );

  if (error) {
    throw new Error(`Failed to fetch student details: ${error.message}`);
  }

  return (Array.isArray(data) ? data : []).map((row) =>
    mapStudentAbsenceDetailRow(row as Record<string, unknown>),
  );
}

// ============================================================================
// Aggregation Helpers
// ============================================================================

/** Class absence totals response from RPC */
export interface ClassAbsenceTotals {
  total_students: number;
  total_absence_days: number;
  total_excused_days: number;
  total_unexcused_days: number;
  total_fehlstunden: number;
  total_lateness_count: number;
  total_ungeklaert: number;
  date_range: { start: string; end: string };
}

/**
 * Fetches class absence totals directly from the database.
 *
 * Uses fn_get_class_absence_totals RPC for server-side aggregation.
 *
 * @param classId - Class ID
 * @param startDate - Optional start date (defaults to current semester)
 * @param endDate - Optional end date (defaults to current semester)
 * @returns Promise resolving to class absence totals
 */
export async function fetchClassAbsenceTotals(
  classId: string,
  startDate?: string,
  endDate?: string,
): Promise<ClassAbsenceTotals | null> {
  const { data, error } = await supabase.rpc("fn_get_class_absence_totals", {
    p_class_id: classId,
    p_start_date: startDate || null,
    p_end_date: endDate || null,
  });

  if (error) {
    console.error("[fetchClassAbsenceTotals] RPC error:", error);
    return null;
  }

  return data as ClassAbsenceTotals;
}
